import React, { useState, useRef, useEffect } from "react";
import { api, friendlyError } from "../lib/api";
import { PageHeader, Disclaimer } from "../components/AppLayout";
import { ShieldCheck, Play, Send, MessageSquare, ClipboardList, FileQuestion } from "lucide-react";
import { toast } from "sonner";

function ListCard({ title, items, icon: Icon }) {
  if (!items?.length) return null;
  return (
    <div className="rounded-xl bg-white border border-slate-200 p-5">
      <h3 className="font-head font-semibold text-sm mb-3 flex items-center gap-2">{Icon && <Icon size={15} className="text-blue-600" />}{title}</h3>
      <ul className="space-y-1.5 text-sm text-slate-600">
        {items.map((it, i) => <li key={i} className="flex gap-2"><span className="text-blue-500">•</span>{typeof it === "string" ? it : it.item}</li>)}
      </ul>
    </div>
  );
}

const SCORE_COLOR = { Adeguata: "#16A34A", Parziale: "#D97706", Insufficiente: "#DC2626" };

export default function Readiness() {
  const [form, setForm] = useState({ authority: "FDA", department: "Aseptic Filling", process: "aseptic filling", product: "sterile injectable" });
  const [busy, setBusy] = useState(false);
  const [plan, setPlan] = useState(null);
  // mock
  const [mockOn, setMockOn] = useState(false);
  const [history, setHistory] = useState([]);
  const [answer, setAnswer] = useState("");
  const [current, setCurrent] = useState(null);
  const [mockBusy, setMockBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history, current]);

  const generate = async () => {
    setBusy(true); setPlan(null);
    try { const { data } = await api.post("/inspection/readiness", form); setPlan(data); }
    catch (e) { toast.error(friendlyError(e)); } finally { setBusy(false); }
  };

  const startMock = async () => {
    setMockOn(true); setHistory([]); setMockBusy(true);
    try {
      const { data } = await api.post("/inspection/mock", { role: `${form.authority} investigator`, history: [], answer: "" });
      setCurrent(data.next_question);
    } catch (e) { toast.error(friendlyError(e)); } finally { setMockBusy(false); }
  };

  const sendAnswer = async () => {
    if (!answer.trim() || mockBusy) return;
    const q = current, a = answer;
    setHistory((h) => [...h, { q, a }]); setAnswer(""); setMockBusy(true);
    try {
      const { data } = await api.post("/inspection/mock", {
        role: `${form.authority} investigator`, history: [...history, { q, a }], answer: a });
      setHistory((h) => h.map((x, i) => i === h.length - 1 ? { ...x, evaluation: data.evaluation, score: data.score } : x));
      setCurrent(data.done ? null : data.next_question);
    } catch (e) { toast.error(friendlyError(e)); } finally { setMockBusy(false); }
  };

  return (
    <div className="ri-fade-in">
      <PageHeader overline="Inspection Readiness AI" title="Inspection Readiness"
        subtitle="Genera focus ispettivo, vulnerabilità, documenti richiesti, mock questions e checklist. Poi allena il team con il mock inspector." />

      <div className="rounded-xl bg-white border border-slate-200 p-5 mb-5 grid sm:grid-cols-4 gap-3">
        <select value={form.authority} onChange={(e) => setForm({ ...form, authority: e.target.value })} data-testid="readiness-authority" className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
          {["FDA", "EMA", "PMDA", "NMPA", "MFDS"].map((a) => <option key={a}>{a}</option>)}
        </select>
        <input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="Reparto" className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
        <input value={form.process} onChange={(e) => setForm({ ...form, process: e.target.value })} placeholder="Processo" className="px-3 py-2 rounded-lg border border-slate-200 text-sm" />
        <div className="flex gap-2">
          <input value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} placeholder="Prodotto" className="flex-1 px-3 py-2 rounded-lg border border-slate-200 text-sm" />
          <button onClick={generate} disabled={busy} data-testid="readiness-generate-btn" className="px-3 rounded-lg bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-60"><Play size={15} /></button>
        </div>
      </div>

      {busy && <div className="text-center py-10 text-slate-400"><div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-3" />Generazione del piano di readiness...</div>}

      {plan && (
        <div className="grid md:grid-cols-2 gap-4">
          <ListCard title="Potential inspection focus" items={plan.potential_focus} icon={ShieldCheck} />
          <ListCard title="Recent regulatory trends" items={plan.recent_trends} />
          <ListCard title="Historical site vulnerabilities" items={plan.historical_vulnerabilities} />
          <ListCard title="Relevant CAPA" items={plan.relevant_capa} />
          <ListCard title="Documenti probabilmente richiesti" items={plan.documents_requested} icon={ClipboardList} />
          <ListCard title="Mock inspection questions" items={plan.mock_questions} icon={FileQuestion} />
          <div className="md:col-span-2">
            <ListCard title="Readiness checklist" items={plan.readiness_checklist} />
          </div>
          <div className="md:col-span-2 rounded-xl bg-slate-900 text-white p-5 flex items-center justify-between flex-wrap gap-3">
            <div><div className="ri-overline" style={{ color: "#64748b" }}>Simula ispezione</div><div className="text-sm text-slate-300 mt-1">L'AI interpreta un {form.authority} investigator. Non sostituisce una vera ispezione.</div></div>
            <button onClick={startMock} data-testid="start-mock-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-sm"><MessageSquare size={16} /> Avvia mock inspection</button>
          </div>
        </div>
      )}

      {mockOn && (
        <div className="mt-5 rounded-xl bg-white border border-slate-200 p-5">
          <h3 className="font-head font-semibold text-sm mb-3">Mock inspection — {form.authority} investigator</h3>
          <div className="space-y-4 max-h-[420px] overflow-y-auto">
            {history.map((h, i) => (
              <div key={i}>
                <div className="text-sm bg-slate-100 rounded-lg px-3 py-2"><b>Ispettore:</b> {h.q}</div>
                <div className="text-sm bg-blue-50 rounded-lg px-3 py-2 mt-1 ml-6"><b>Tu:</b> {h.a}</div>
                {h.evaluation && (
                  <div className="text-xs mt-1 ml-6 flex items-start gap-2">
                    {h.score && <span className="px-2 py-0.5 rounded-full text-white text-[0.65rem]" style={{ background: SCORE_COLOR[h.score] || "#64748b" }}>{h.score}</span>}
                    <span className="text-slate-500">{h.evaluation}</span>
                  </div>
                )}
              </div>
            ))}
            {current && <div className="text-sm bg-slate-100 rounded-lg px-3 py-2" data-testid="mock-question"><b>Ispettore:</b> {current}</div>}
            {mockBusy && <div className="text-slate-400 text-sm flex items-center gap-2"><div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full" /> ...</div>}
            <div ref={endRef} />
          </div>
          {current && (
            <form onSubmit={(e) => { e.preventDefault(); sendAnswer(); }} className="mt-3 flex gap-2">
              <input value={answer} onChange={(e) => setAnswer(e.target.value)} data-testid="mock-answer-input" placeholder="La tua risposta all'ispettore..." className="flex-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" />
              <button type="submit" disabled={mockBusy} data-testid="mock-send-btn" className="px-4 rounded-lg bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-60"><Send size={16} /></button>
            </form>
          )}
          {!current && history.length > 0 && <Disclaimer text="Sessione mock terminata. Non sostituisce una vera ispezione: usare per training interno." />}
        </div>
      )}
    </div>
  );
}
